import React from 'react';
import { Capacitor } from '@capacitor/core';
import {
  useGridApiContext,
  gridFilteredSortedRowIdsSelector,
  gridColumnDefinitionsSelector,
  type GridValidRowModel,
  ToolbarButton,
} from '@mui/x-data-grid-premium';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';

import { CapacitorBridge } from '@/services/CapacitorBridge';
import { ALLOWED_EXTENSIONS } from '../utils/fileValidation';
import { getMimeTypeFromFilename } from '@/utils/fileValidation';
import { createTypedBlob } from '@/utils/triggerBlobDownload';

type ExportCellValue = string | number | boolean | Date;

type GridApiRef = ReturnType<typeof useGridApiContext>;

const getExportColumns = (apiRef: GridApiRef) =>
  gridColumnDefinitionsSelector(apiRef)
    .filter((column) => column.disableExport !== true)
    .map((column) => ({
      field: column.field,
      headerName: column.headerName || column.field,
    }));

const getCsv = (apiRef: GridApiRef): string => {
  const rowIds = gridFilteredSortedRowIdsSelector(apiRef);
  const columns = getExportColumns(apiRef);

  const csv: string[] = [];
  csv.push(columns.map((col) => `"${col.headerName}"`).join(','));

  rowIds.forEach((id) => {
    const row = columns.map((col) => {
      const val =
        apiRef.current.getCellParams<
          GridValidRowModel,
          ExportCellValue | null | undefined
        >(id, col.field).value ?? '';
      return `"${String(val).replace(/"/g, '""')}"`;
    });
    csv.push(row.join(','));
  });

  return csv.join('\n');
};

const getExcelBase64 = (apiRef: GridApiRef): string => {
  const rowIds = gridFilteredSortedRowIdsSelector(apiRef);
  const columns = getExportColumns(apiRef);

  const headerRow = columns.map((col) => col.headerName);

  const dataRows = rowIds.map((id) =>
    columns.map(
      (col) =>
        apiRef.current.getCellParams<
          GridValidRowModel,
          ExportCellValue | null | undefined
        >(id, col.field).value ?? '',
    ),
  );

  const worksheetData = [headerRow, ...dataRows];

  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

  return String(
    XLSX.write(workbook, {
      type: 'base64',
      bookType: 'xlsx',
    }),
  );
};

async function saveAndOpenFile(
  filename: string,
  data: string,
  options?: { base64?: boolean },
) {
  if (Capacitor.getPlatform() === 'web') {
    const mimeType = getMimeTypeFromFilename(filename);
    const blob = options?.base64
      ? createTypedBlob(
          Uint8Array.from(atob(data), (c) => c.charCodeAt(0)),
          filename,
          mimeType,
        )
      : createTypedBlob(data, filename, mimeType);

    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
    return;
  }

  try {
    await CapacitorBridge.execute<undefined>('requestPermissions');
  } catch (e) {
    console.warn('Permission request failed or rejected', e);
  }

  const { uri } = await CapacitorBridge.execute<{ uri: string }>('writeFile', {
    path: filename,
    data,
    encoding: options?.base64 ? undefined : CapacitorBridge.Encoding.UTF8,
    isBinary: options?.base64 ?? false,
  });

  if (Capacitor.getPlatform() === 'ios') {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext && !ALLOWED_EXTENSIONS.includes(ext)) {
      await CapacitorBridge.execute<undefined>('share', {
        title: filename,
        url: uri,
      });
      return;
    }
  }

  try {
    await CapacitorBridge.execute<undefined>('openFile', {
      filePath: uri,
    });
  } catch (error) {
    if (
      error instanceof Error &&
      'code' in error &&
      error.code === 'OS-PLUG-FLVW-0010'
    ) {
      const { dispatchFileOpenError } =
        await import('./common/FileOpenErrorDialog');
      dispatchFileOpenError(filename, uri);
    } else {
      throw error;
    }
  }
}

function ExportCsv({ onClose }: { onClose: () => void }) {
  const apiRef = useGridApiContext();

  const handleExport = async () => {
    const csv = getCsv(apiRef);
    await saveAndOpenFile('Wealth Access.csv', csv, { base64: false });
    onClose();
  };

  return (
    <MenuItem onClick={() => void handleExport()}>Download as CSV</MenuItem>
  );
}

function ExportExcel({ onClose }: { onClose: () => void }) {
  const apiRef = useGridApiContext();

  const handleExport = async () => {
    const base64 = getExcelBase64(apiRef);
    await saveAndOpenFile('Wealth Access.xlsx', base64, { base64: true });
    onClose();
  };

  return (
    <MenuItem onClick={() => void handleExport()}>Download as Excel</MenuItem>
  );
}

export default function MobileExportButton() {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement | null>(null);

  return (
    <>
      <ToolbarButton
        ref={anchorRef}
        onClick={() => {
          setOpen(true);
        }}
      >
        <FileDownloadIcon fontSize='small' />
      </ToolbarButton>

      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      >
        <ExportCsv
          onClose={() => {
            setOpen(false);
          }}
        />
        <ExportExcel
          onClose={() => {
            setOpen(false);
          }}
        />
      </Menu>
    </>
  );
}
